/**
 * Submit the survey until the server has answered with a code.
 *
 * The answer that matters is `studyComplete`, not the acknowledgement: an
 * ack says the server has the answers, the code says it has finished with
 * them. So the submission is repeated — the server stores it once however
 * often it arrives — until the code has come back, the server refuses it for
 * a reason a retry cannot fix, or the attempts run out.
 */

export type SurveyAck = { ok: boolean; error?: string } | 'timeout'

export interface SurveyRetryOptions {
  /** One submission; resolves with the ack, or `'timeout'` after `ackTimeoutMs`. */
  send(): Promise<SurveyAck>
  /** Has the code arrived? */
  isDone(): boolean
  maxAttempts: number
  /** How long to wait after an attempt before the next. */
  retryDelayMs: number
  sleep?: (ms: number) => Promise<void>
  onRetry?: (attempt: number) => void
}

export type SurveyRetryOutcome = 'done' | 'refused' | 'gave_up'

export async function submitUntilComplete(options: SurveyRetryOptions): Promise<{ outcome: SurveyRetryOutcome; attempts: number; error?: string }> {
  const sleep = options.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)))
  for (let attempt = 1; attempt <= options.maxAttempts; attempt += 1) {
    if (options.isDone()) return { outcome: 'done', attempts: attempt - 1 }
    if (attempt > 1) options.onRetry?.(attempt)
    const ack = await options.send()
    if (options.isDone()) return { outcome: 'done', attempts: attempt }
    if (ack !== 'timeout' && !ack.ok) return { outcome: 'refused', attempts: attempt, error: ack.error }
    await sleep(options.retryDelayMs)
    if (options.isDone()) return { outcome: 'done', attempts: attempt }
  }
  return { outcome: options.isDone() ? 'done' : 'gave_up', attempts: options.maxAttempts }
}
